import { z } from "zod";

import type {
  Filter,
  FilterMatchMode,
} from "@/shared/components/filters/FilterSchema";
import { RoboqlQuery, type QueryTarget } from "@/shared/domain/query";
import { filterSchema } from "@/shared/domain/query/filters";
import { VIEW_SCHEME_V1, type ViewRecord } from "@/shared/domain/views";
import type { DataTableSort } from "@/shared/foundation/primitives/Table";
import type { SearchQueryBody } from "@/types";

import type { ActiveFilters as ActiveFiltersState } from "../Filters/FilterModel";
import type { ResourceView, UseResourceViewOptions } from "../useResourceView";

/**
 * What a resource table remembers about itself for the life of the tab.
 *
 * The live state, not the wire shape a View is saved as. A snapshot is read by
 * the same client that wrote it, so nothing needs projecting for a server, and
 * keeping the live shape means a RoboQL filter keeps its transpiled `spec`:
 * RoboQL has no relative-date syntax, so the spec cannot go stale, and holding
 * it is what lets a RoboQL table restore at mount without a round trip.
 *
 * `loadedView` is the whole record rather than an id for the same reason:
 * `useLoadedView` can mark a record loaded on the first render, but an id would
 * have to be fetched first, and the table would paint unloaded in the meantime.
 * The cost is that a View a teammate edits while this tab is open is judged
 * dirty against the copy taken here until it is selected again.
 *
 * An empty table is a snapshot too — `filters: { type: "none" }` — and is not
 * the same as no snapshot. Clearing back to unfiltered is a decision, and one
 * that must outrank the default View on the next visit.
 */
/**
 * Filter state as stored. The same shape as the live state except that a
 * RoboQL query is its text: `RoboqlQuery` is a class, and a class does not
 * survive JSON. It is rebuilt with `RoboqlQuery.from` on the way back.
 */
export type StoredFilters =
  | { type: "none" }
  | { type: "structured"; filters: Filter[]; matchMode: FilterMatchMode }
  | { type: "roboql"; query: string; spec: SearchQueryBody };

export interface TableSnapshot {
  version: typeof TABLE_MEMORY_VERSION;
  filters: StoredFilters;
  sort: DataTableSort | null;
  pageSize: number;
  visibleColumns: string[];
  loadedView: ViewRecord | null;
  /**
   * The sort `loadedView`'s query carried when it was applied: its `SORT BY`
   * for a RoboQL View, `null` otherwise. Restored alongside the View, because
   * a View restored from memory is never re-applied, and without this the
   * session would take such a View to show the list's default sort and judge
   * it dirty, or retire a draft, on the strength of that.
   */
  loadedQuerySort: DataTableSort | null;
}

/**
 * Bumped whenever the stored shape changes. A snapshot under any other version
 * reads as absent: storage outlives the build that wrote it, and a deploy
 * mid-session must not hand the new client a shape it does not understand.
 */
export const TABLE_MEMORY_VERSION = 1;

/**
 * Keyed by org, target and route. The same list component serves a top-level
 * page and parent-scoped tabs — a dataset's Sessions, a collection's Files —
 * and those are different tables: different scope, sometimes different filter
 * fields. Sharing one memory between them would restore one table's filters
 * onto another.
 */
export function memoryKey(
  orgId: string,
  target: QueryTarget,
  pathname: string,
): string {
  return `roboto.table-memory.${orgId}.${target}.${pathname}`;
}

const sortSchema = z.object({
  columnId: z.string(),
  direction: z.enum(["asc", "desc"]),
});

const filtersSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("none") }),
  z.object({
    type: z.literal("structured"),
    filters: z.array(filterSchema),
    matchMode: z.enum(["AND", "OR"]),
  }),
  z.object({
    type: z.literal("roboql"),
    query: z.string(),
    spec: z.object({}).passthrough(),
  }),
]);

const loadedViewSchema = z
  .object({
    view_id: z.string(),
    name: z.string(),
    org_id: z.string(),
    target: z.string(),
    definition: z.object({ scheme: z.literal(VIEW_SCHEME_V1) }).passthrough(),
  })
  .passthrough();

const snapshotSchema = z.object({
  version: z.literal(TABLE_MEMORY_VERSION),
  filters: filtersSchema,
  sort: sortSchema.nullable(),
  pageSize: z.number().int().positive(),
  visibleColumns: z.array(z.string()),
  loadedView: loadedViewSchema.nullable(),
  // Added after version 1 shipped. Absent in a snapshot written before it,
  // which reads as "no query sort" — the right answer for every View but a
  // RoboQL one with an unstated display sort, and for that one the cost is a
  // single visit judged against the default sort. No version bump, which
  // would have cost every open tab its memory instead.
  loadedQuerySort: sortSchema.nullable().default(null),
});

/**
 * The snapshot stored for this org and table, or `null` when there is none
 * — or when what is there cannot be trusted. `sessionStorage` is a trust
 * boundary: it can hold a shape an older build wrote, or nothing this build
 * has ever seen, and either has to read as "start from the default" rather
 * than as malformed state seeded into a table.
 */
export function readSnapshot(
  orgId: string,
  target: QueryTarget,
  pathname: string,
): TableSnapshot | null {
  try {
    const raw = sessionStorage.getItem(memoryKey(orgId, target, pathname));
    if (raw === null) {
      return null;
    }
    const parsed = snapshotSchema.safeParse(JSON.parse(raw));
    return parsed.success ? (parsed.data as TableSnapshot) : null;
  } catch {
    return null;
  }
}

export function writeSnapshot(
  orgId: string,
  target: QueryTarget,
  pathname: string,
  snapshot: TableSnapshot,
): void {
  try {
    sessionStorage.setItem(
      memoryKey(orgId, target, pathname),
      JSON.stringify(snapshot),
    );
  } catch {
    // Quota or disabled storage: the table simply does not remember.
  }
}

function toStored(filters: ActiveFiltersState): StoredFilters {
  return filters.type === "roboql"
    ? { ...filters, query: filters.query.toString() }
    : filters;
}

function fromStored(filters: StoredFilters): ActiveFiltersState {
  return filters.type === "roboql"
    ? { ...filters, query: RoboqlQuery.from(filters.query) }
    : filters;
}

/** Capture what the table is showing right now. */
export function snapshotOf<TData>(
  view: ResourceView<TData>,
  loadedView: ViewRecord | null,
  loadedQuerySort: DataTableSort | null = null,
): TableSnapshot {
  return {
    version: TABLE_MEMORY_VERSION,
    filters: toStored(view.filters),
    sort: view.table.sort,
    pageSize: view.table.pageSize,
    visibleColumns: [...view.table.visibleColumnIds],
    loadedView,
    loadedQuerySort,
  };
}

/**
 * Merge a snapshot into the options a list hands to `useResourceView`, so the
 * table starts out showing what it showed before rather than starting empty
 * and being corrected a moment later. `useResourceView` reads these options
 * once, on mount, so this is the one chance to start the table in the right
 * state — and it is synchronous, which is what keeps the restore from ever
 * racing an apply.
 *
 * Unlike a View, a snapshot is live state, so `sort: null` means the table
 * was unsorted and restores as unsorted rather than as the list's default.
 * Columns are the one exception: an empty list is what a table reports before
 * any column preference exists, while its ColDef defaults are what is actually
 * visible, so seeding `[]` back would hide every column. It defers to the
 * list's own columns instead, the same reading `definitionToViewState` gives an
 * empty `visible_columns`.
 */
export function seedOptionsFromSnapshot<TData>(
  snapshot: TableSnapshot,
  options: UseResourceViewOptions<TData>,
): UseResourceViewOptions<TData> {
  return {
    ...options,
    initialActiveFilters: fromStored(snapshot.filters),
    initialSort: snapshot.sort ?? undefined,
    preferences: {
      columns: snapshot.visibleColumns.length
        ? snapshot.visibleColumns
        : options.preferences?.columns,
      rowsPerPage: snapshot.pageSize,
    },
  };
}
